import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Divider,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  CheckCircle,
  ErrorOutline,
} from '@mui/icons-material';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { Customer } from '../../services/customerService';

// ---------------------------------------------------------------------------
// Sidebar "Credit Status" card for the invoice workbench. Invoice-shaped
// counterpart to CreditAvailabilityPanel's credit-control half (that
// component is hard-wired to a Sales Order id + its /availability endpoint;
// an invoice being built manually or against a Delivery doesn't necessarily
// have one). Sources ONLY real data:
//   GET /sales/customers/:id/credit-status?amount=<current invoice total>
// Customer/tax detail display (GSTIN, address, place of supply) lives in the
// main-workspace "Customer & Tax Details" section instead of here, so this
// card doesn't duplicate that lookup.
// ---------------------------------------------------------------------------

interface CreditStatus {
  creditLimit: number;
  outstanding: number;
  overdue: number;
  openOrderValue: number;
  currentOrder: number;
  projectedExposure: number;
  availableCredit: number;
  utilizationPct: number | null;
  status: 'NO_LIMIT_SET' | 'WITHIN_LIMIT' | 'EXCEEDED';
}

const Row: React.FC<{ label: string; value: React.ReactNode; strong?: boolean; tone?: string }> = ({
  label,
  value,
  strong,
  tone,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 600, color: tone }}>
      {value}
    </Typography>
  </Box>
);

interface Props {
  customer: Customer | null;
  invoiceAmount: number;
  /** Called when the exceeded/within-limit state changes, so the parent form can gate the action bar. */
  onCreditStatusChange?: (status: CreditStatus | null) => void;
}

const CreditStatusCard: React.FC<Props> = ({ customer, invoiceAmount, onCreditStatusChange }) => {
  const theme = useTheme();
  const [credit, setCredit] = useState<CreditStatus | null>(null);
  const [creditErr, setCreditErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customer) {
      setCredit(null);
      setCreditErr(null);
      onCreditStatusChange?.(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setCreditErr(null);
    api
      .get(`/sales/customers/${customer.id}/credit-status?amount=${encodeURIComponent(invoiceAmount || 0)}`)
      .then((res: any) => {
        if (!alive) return;
        const c = res?.credit ?? res ?? null;
        setCredit(c);
        onCreditStatusChange?.(c);
      })
      .catch((e: any) => {
        if (!alive) return;
        setCreditErr(e?.response?.data?.message || e?.message || 'Could not load credit status');
        onCreditStatusChange?.(null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, invoiceAmount]);

  const creditTone =
    credit?.status === 'EXCEEDED'
      ? theme.palette.error.main
      : credit?.status === 'NO_LIMIT_SET'
      ? theme.palette.text.secondary
      : theme.palette.success.main;

  const util = credit?.utilizationPct ?? 0;
  const utilBarColor =
    util >= 100 ? theme.palette.error.main : util >= 80 ? theme.palette.warning.main : theme.palette.success.main;

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
      {!customer ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AccountBalanceWallet fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              Credit Status
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Select a customer to see credit exposure.
          </Typography>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccountBalanceWallet fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              Credit Status
            </Typography>
            {credit && (
              <Chip
                size="small"
                label={credit.status === 'EXCEEDED' ? 'Limit Exceeded' : credit.status === 'NO_LIMIT_SET' ? 'No Limit Set' : 'Within Limit'}
                icon={credit.status === 'EXCEEDED' ? <ErrorOutline sx={{ fontSize: 16 }} /> : credit.status === 'NO_LIMIT_SET' ? undefined : <CheckCircle sx={{ fontSize: 16 }} />}
                sx={{ fontWeight: 700, color: creditTone, bgcolor: `${creditTone}1f`, '& .MuiChip-icon': { color: creditTone } }}
              />
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : creditErr ? (
            <Typography variant="body2" color="text.secondary">
              {creditErr}
            </Typography>
          ) : credit ? (
            <>
              <Row label="Credit Limit" value={credit.creditLimit > 0 ? formatIndianNumber(credit.creditLimit) : '—'} />
              <Row label="Outstanding" value={formatIndianNumber(credit.outstanding)} />
              {credit.overdue > 0 && <Row label="of which Overdue" value={formatIndianNumber(credit.overdue)} tone={theme.palette.error.main} />}
              <Row label="Current Invoice" value={formatIndianNumber(invoiceAmount)} />
              <Divider sx={{ my: 1 }} />
              <Row label="Projected Exposure" value={formatIndianNumber(credit.projectedExposure)} strong />
              <Row
                label="Available Credit"
                value={credit.creditLimit > 0 ? formatIndianNumber(credit.availableCredit) : '—'}
                strong
                tone={credit.availableCredit < 0 ? theme.palette.error.main : theme.palette.success.main}
              />
              {credit.utilizationPct != null && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Utilisation</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: utilBarColor }}>{credit.utilizationPct.toFixed(0)}%</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(util, 100)}
                    sx={{ height: 7, borderRadius: 4, bgcolor: `${utilBarColor}22`, '& .MuiLinearProgress-bar': { backgroundColor: utilBarColor, borderRadius: 4 } }}
                  />
                </Box>
              )}
              {credit.status === 'EXCEEDED' && (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: `${theme.palette.error.main}12`, border: '1px solid', borderColor: 'error.main' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main', display: 'block' }}>
                    ⚠ CREDIT LIMIT EXCEEDED by {formatIndianNumber(Math.abs(credit.availableCredit))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    You can still create this invoice, but it will be flagged for approval on save.
                  </Typography>
                </Box>
              )}
            </>
          ) : null}
        </>
      )}
    </Paper>
  );
};

export default CreditStatusCard;
